import { Channel, ScheduledMessage, UserRole, GlobalRole } from '../types';
import { apiFetch } from './apiClient';

function fetchAdmin<T>(endpoint: string, options?: RequestInit): Promise<T> {
  return apiFetch<T>(`/admin${endpoint}`, options);
}

export interface AdminChannel extends Channel {
  lastMessageAt?: string | null;
  affiliateId?: string | null;
}

/** 공지 예약 행 — 예약자가 아니어도 권한 있는 관리자면 취소·수정할 수 있다 */
export interface AdminScheduledMessage extends ScheduledMessage {
  channelName: string | null;
  channelAffiliateId: string | null;
  /** 예약한 관리자의 실명 (발송 시 표시명은 '관리자') */
  senderName: string;
}

export interface BlockedExtension {
  extension: string;
  description: string | null;
  createdBy: string;
  createdByName: string;
  createdAt: string;
}

/** 코드에 박힌 절대 차단 확장자 — 관리자도 해제할 수 없다 */
export interface AlwaysBlockedExtension {
  extension: string;
  description: string;
}

export interface PushChannelCounters {
  attempted: number;
  sent: number;
  expired: number;
  suppressed: number;
  retried: number;
  failed: number;
  byStatus: Record<string, number>;
}

export interface PushFailureSample {
  at: string;
  channel: string;
  status: string;
  message: string;
  origin?: string;
  type?: string;
}

/** 서버 프로세스 시작 이후 누적. 재시작하면 0으로 돌아간다(since로 관측 창을 판단할 것). */
export interface PushMetrics {
  since: string;
  channels: Record<string, PushChannelCounters>;
  recentFailures: PushFailureSample[];
}

export interface ClientVersionRow {
  userId: string;
  userName: string | null;
  appVersion: string | null;
  lastSeenAt: string | null;
  updatedAt: string | null;
}

/** 설치 허용 IP 예외 1건 */
export interface InstallAllowlistEntry {
  value: string;
  note: string;
  addedBy: string;
  addedByName: string;
  addedAt: string | null;
}

export interface InstallAllowlistState {
  gateEnabled: boolean;
  baseCidrs: string[];
  entries: InstallAllowlistEntry[];
  updatedAt: string | null;
}

/** PC 앱 미설치 승인 직원 리포트 */
export interface MissingDesktopReport {
  affiliateId: string;
  approvedTotal: number;
  missing: Array<{ userId: string; userName: string | null; depart: string | null }>;
}

/** 로그인 승인 명단의 한 행 */
export interface LoginApprovalRow {
  affiliateId: string;
  /** 사번 */
  userId: string;
  /** 인트라넷 아이디 */
  userid: string | null;
  userName: string | null;
  depart: string | null;
  approvedBy: string | null;
  approvedByName: string | null;
  createdAt: string;
}

/** 승인제 스위치 상태 */
export interface LoginApprovalPolicy {
  enabled: boolean;
  /** .env의 LOGIN_APPROVAL_ENABLED=false로 강제 해제된 상태 (관리자 콘솔에서 켤 수 없다) */
  envKillSwitch: boolean;
  updatedBy: string;
  updatedByName: string;
  updatedAt: string | null;
}

export interface LoginApprovalState extends LoginApprovalPolicy {
  affiliateId: string;
  approvals: LoginApprovalRow[];
  /** super_admin 여부 — 다른 계열사 명단까지 다룰 수 있는지 (JWT의 role은 클라이언트에 없다) */
  canManageAllAffiliates: boolean;
}

// ─── 기기 신뢰 (server/services/deviceTrust.js serializeRow) ───
export type DeviceListStatus = 'pending' | 'approved' | 'closed' | 'all';

export interface AdminDeviceRow {
  id: number;
  affiliateId: string;
  userId: string;
  userid: string | null;
  userName: string;
  depart: string;
  kind: 'desktop' | 'web';
  fingerprint: string;
  status: 'pending' | 'approved' | 'rejected' | 'revoked' | 'expired';
  label: string;
  platform: { ua?: string; platform?: string; standalone?: boolean; mobile?: boolean; screenW?: number; screenH?: number };
  computerName: string;
  requestIp: string | null;
  /** 요청 IP가 사내망(INSTALL_ALLOWED_CIDRS)인가 */
  officeNetwork: boolean | null;
  selfConfirmed: boolean;
  verifyAttempts: number;
  requestedAt: string;
  decidedAt: string | null;
  decidedByName: string | null;
  decisionReason: string | null;
  autoReason: 'internal' | 'migration' | null;
  lastSeenAt: string | null;
  lastSeenIp: string | null;
  /** 지금 이 관리자 세션의 기기 — 해제할 수 없다 */
  isMine: boolean;
}

export interface DeviceListResponse {
  rows: AdminDeviceRow[];
  counts: { pending: number; approved: number; closed: number };
}

export interface DeviceTrustPolicy {
  mode: 'off' | 'observe' | 'enforce';
  configuredMode?: 'off' | 'observe' | 'enforce';
  envKillSwitch?: boolean;
  desktopAutoApproveInternal: boolean;
  maxAutoDesktopsPerUser: number;
  maxWebDevicesPerUser: number;
  webResumeDays: number;
  webInactivityDays: number;
  pendingTtlDays: number;
  migrationCutoff: string | null;
  updatedByName?: string;
  updatedAt?: string | null;
}

export interface DeviceReadinessUser { userId: string; userName: string; appVersion: string; lastSeenAt: string }

export interface DeviceReadiness {
  recentUsers: number;
  lockedOut: DeviceReadinessUser[];
  pcWithoutDevice: DeviceReadinessUser[];
  webOnly: DeviceReadinessUser[];
  pending: number;
  adminsWithoutDesktop: { userId: string; role: string }[];
  helpdeskWithoutDesktop: { userId: string; userName: string }[];
  legacyDesktop: { aff: string; sabun: string; name: string; at: number }[];
  requesterReady: boolean;
}

/** 승인 요청에 실어 보내는 최소 정보 (표시용 이름·부서는 역정규화 저장) */
export interface LoginApprovalInput {
  userId: string;
  userid?: string;
  name?: string;
  depart?: string;
}

/** 관리자가 넘을 수 없는 허용 범위 — 서버(server/services/uploadPolicy.js)가 내려준다 */
export interface UploadPolicyLimits {
  minFileSizeMB: number;
  maxFileSizeMB: number;
  minFiles: number;
  maxFiles: number;
  minRetentionDays: number;
  maxRetentionDays: number;
}

export interface UploadPolicyAdmin {
  maxFileSizeMB: number;
  maxFiles: number;
  /** 첨부 보관 기간(일). 0 = 만료 없음 */
  retentionDays: number;
  updatedBy: string;
  updatedByName: string;
  updatedAt: string | null;
  limits: UploadPolicyLimits;
}

/** 원격지원 정책 — enabled/controlEnabled/fileTransferEnabled는 env 킬스위치까지 반영한 실제 적용값 */
export interface RemoteSupportPolicy {
  enabled: boolean;
  controlEnabled: boolean;
  /** 원격지원 중 파일 주고받기. 구버전 서버는 없다 */
  fileTransferEnabled?: boolean;
  /** 저장된 값(킬스위치로 덮이기 전) — 토글 버튼은 이 값을 바꾼다 */
  configured: { enabled: boolean; controlEnabled: boolean; fileTransferEnabled?: boolean };
  /** .env REMOTE_SUPPORT_ENABLED=false — 콘솔에서 켤 수 없다 */
  envKillSwitch: boolean;
  /** .env REMOTE_SUPPORT_CONTROL_ENABLED=false — 보기는 되고 제어만 막힌다 */
  envControlKillSwitch: boolean;
  /** .env REMOTE_SUPPORT_FILE_TRANSFER_ENABLED=false — 파일 주고받기만 막힌다 */
  envFileKillSwitch?: boolean;
  updatedBy: string;
  updatedByName: string;
  updatedAt: string | null;
}

/** 원격지원 담당자(헬프데스크) 명단의 한 행 */
export interface HelpdeskOperatorRow {
  affiliateId: string;
  /** 사번 */
  userId: string;
  userName: string | null;
  depart: string | null;
  grantedBy: string | null;
  grantedByName: string | null;
  createdAt: string;
}

export interface RemoteSupportAdminState {
  policy: RemoteSupportPolicy;
  operators: HelpdeskOperatorRow[];
  /** super_admin — 명단에 없어도 자동으로 담당자다(2026-10-08~). 구버전 서버는 없다 */
  superAdmins?: { userId: string; userName: string | null }[];
}

/** 회사 일정 담당자 명단의 한 행 — 자기 계열사 회사 일정을 등록·수정·삭제한다 */
export interface CalendarEditorRow {
  affiliateId: string;
  /** 사번 */
  userId: string;
  userName: string | null;
  depart: string | null;
  grantedBy: string | null;
  grantedByName: string | null;
  createdAt: string;
}

export interface CalendarEditorsAdminState {
  editors: CalendarEditorRow[];
  /** super_admin — 명단에 없어도 모든 회사 일정을 관리한다('자동' 행) */
  superAdmins: { userId: string; userName: string | null }[];
}

/** 관리자 콘솔 세션 이력의 한 행 (super_admin — 기기·IP·입력 건수까지) */
export interface RemoteSupportSessionAdminRow {
  id: string;
  sessionCode: string;
  operatorId: string;
  operatorName: string;
  operatorAffiliateId: string | null;
  hostId: string;
  hostName: string;
  hostAffiliateId: string | null;
  hostDeviceId: string | null;
  hostIp: string | null;
  monitorLabel: string | null;
  requestedAt: string;
  acceptedAt: string | null;
  endedAt: string | null;
  durationSeconds: number | null;
  controlSeconds: number;
  inputEvents: number;
  endReason: string | null;
  endedBy: string | null;
  /** 'host' = 직원이 먼저 요청 */
  initiatedBy?: 'host' | 'operator';
  /** 주고받은 파일 수(완료만) — 담당자 → 직원 / 직원 → 담당자. 구버전 서버는 없다 */
  filesToHost?: number;
  filesToOperator?: number;
  note: string | null;
}

export interface RemoteSupportSessionList {
  days: number;
  sessions: RemoteSupportSessionAdminRow[];
  summary: {
    count: number;
    avgDurationSeconds: number;
    totalControlSeconds: number;
    withControl: number;
  };
}

export const adminService = {
  // Channel Management
  async getPublicChannels(): Promise<AdminChannel[]> {
    return fetchAdmin<AdminChannel[]>('/channels');
  },

  async createPublicChannel(
    name: string,
    description?: string,
    affiliateId?: string | null
  ): Promise<AdminChannel> {
    return fetchAdmin<AdminChannel>('/channels', {
      method: 'POST',
      body: JSON.stringify({ name, description, affiliateId: affiliateId ?? null }),
    });
  },

  async deletePublicChannel(channelId: number): Promise<{ success: boolean }> {
    return fetchAdmin(`/channels/${channelId}`, {
      method: 'DELETE',
    });
  },

  // Admin Messenger
  async sendMessage(
    channelId: number,
    content: string,
    msgType: string = 'text'
  ): Promise<any> {
    return fetchAdmin(`/channels/${channelId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ content, msgType }),
    });
  },

  // 공지 예약 (발송은 서버 스윕이 즉시 발송 라우트와 동등하게 수행)
  async scheduleMessage(channelId: number, content: string, scheduledAt: string): Promise<AdminScheduledMessage> {
    return fetchAdmin<AdminScheduledMessage>(`/channels/${channelId}/scheduled-messages`, {
      method: 'POST',
      body: JSON.stringify({ content, scheduledAt }),
    });
  },

  async listScheduledMessages(): Promise<AdminScheduledMessage[]> {
    return fetchAdmin<AdminScheduledMessage[]>('/scheduled-messages');
  },

  async updateScheduledMessage(id: string, content: string, scheduledAt: string): Promise<AdminScheduledMessage> {
    return fetchAdmin<AdminScheduledMessage>(`/scheduled-messages/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ content, scheduledAt }),
    });
  },

  /** 대기 중이면 취소, 실패 행이면 목록에서 제거 */
  async cancelScheduledMessage(id: string): Promise<AdminScheduledMessage> {
    return fetchAdmin<AdminScheduledMessage>(`/scheduled-messages/${id}`, {
      method: 'DELETE',
    });
  },

  // Admin User Management
  async getAdmins(): Promise<SystemAdmin[]> {
    return fetchAdmin<SystemAdmin[]>('/admins');
  },

  async addAdmin(userId: string, userName?: string): Promise<{ success: boolean }> {
    return fetchAdmin('/admins', {
      method: 'POST',
      body: JSON.stringify({ userId, userName }),
    });
  },

  async updateAdmin(userId: string, userName: string): Promise<SystemAdmin> {
    return fetchAdmin<SystemAdmin>(`/admins/${encodeURIComponent(userId)}`, {
      method: 'PUT',
      body: JSON.stringify({ userName }),
    });
  },

  async removeAdmin(userId: string): Promise<{ success: boolean }> {
    return fetchAdmin(`/admins/${encodeURIComponent(userId)}`, {
      method: 'DELETE',
    });
  },

  // Role Management
  async getRoles(): Promise<UserRole[]> {
    return fetchAdmin<UserRole[]>('/roles');
  },

  async grantRole(userId: string, role: GlobalRole, userName?: string): Promise<UserRole> {
    return fetchAdmin<UserRole>('/roles', {
      method: 'POST',
      body: JSON.stringify({ userId, role, userName }),
    });
  },

  async revokeRole(userId: string): Promise<{ success: boolean }> {
    return fetchAdmin(`/roles/${encodeURIComponent(userId)}`, {
      method: 'DELETE',
    });
  },

  // Blocked File Extensions
  async getBlockedExtensions(): Promise<BlockedExtension[]> {
    return fetchAdmin<BlockedExtension[]>('/file-extensions/blocked');
  },

  // 관리 목록과 합쳐야 실제로 차단되는 전체 집합이 된다 (한쪽만 보여주면 "목록에 없는데 막힌다")
  async getAlwaysBlockedExtensions(): Promise<AlwaysBlockedExtension[]> {
    return fetchAdmin<AlwaysBlockedExtension[]>('/file-extensions/always-blocked');
  },

  async addBlockedExtension(extension: string, description?: string): Promise<BlockedExtension> {
    return fetchAdmin<BlockedExtension>('/file-extensions/blocked', {
      method: 'POST',
      body: JSON.stringify({ extension, description }),
    });
  },

  async removeBlockedExtension(extension: string): Promise<{ success: boolean }> {
    return fetchAdmin(`/file-extensions/blocked/${encodeURIComponent(extension)}`, {
      method: 'DELETE',
    });
  },

  // Client Version Tracking
  async getClientVersions(): Promise<ClientVersionRow[]> {
    return fetchAdmin<ClientVersionRow[]>('/clients');
  },

  /** PC 앱 미설치 승인 직원 목록 (전사 배포 진행 추적) */
  async getMissingDesktop(affiliateId?: string): Promise<MissingDesktopReport> {
    const query = affiliateId ? `?affiliateId=${encodeURIComponent(affiliateId)}` : '';
    return fetchAdmin<MissingDesktopReport>(`/clients/missing-desktop${query}`);
  },

  // Install IP Allowlist (설치 허용 IP — super_admin 전용)
  async getInstallAllowlist(): Promise<InstallAllowlistState> {
    return fetchAdmin<InstallAllowlistState>('/install-allowlist');
  },

  async addInstallAllowlist(value: string, note?: string): Promise<{ success: boolean; entry: InstallAllowlistEntry }> {
    return fetchAdmin('/install-allowlist', {
      method: 'POST',
      body: JSON.stringify({ value, note }),
    });
  },

  async removeInstallAllowlist(value: string): Promise<{ success: boolean }> {
    return fetchAdmin(`/install-allowlist/${encodeURIComponent(value)}`, {
      method: 'DELETE',
    });
  },

  // Upload Policy (첨부 용량·개수)
  async getUploadPolicy(): Promise<UploadPolicyAdmin> {
    return fetchAdmin<UploadPolicyAdmin>('/upload-policy');
  },

  // 숫자 두 개가 나란히 놓이는 자리라 순서 혼동을 막으려고 객체로 받는다
  async updateUploadPolicy(input: {
    maxFileSizeMB: number;
    maxFiles: number;
    retentionDays: number;
    reason?: string;
  }): Promise<UploadPolicyAdmin> {
    return fetchAdmin<UploadPolicyAdmin>('/upload-policy', {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  },

  // Login Approvals (로그인 승인 명단)
  async getLoginApprovals(affiliateId?: string): Promise<LoginApprovalState> {
    const query = affiliateId ? `?affiliateId=${encodeURIComponent(affiliateId)}` : '';
    return fetchAdmin<LoginApprovalState>(`/login-approvals${query}`);
  },

  /** 여러 명을 한 번에 승인 (단건도 배열 1개로 보낸다) */
  async grantLoginApprovals(input: {
    affiliateId: string;
    users: LoginApprovalInput[];
  }): Promise<{ success: boolean; added: number; requested: number }> {
    return fetchAdmin('/login-approvals', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  /** 이미 접속한 적이 있는 사용자를 한 번에 승인 (승인제 전환용) */
  async seedActiveLoginApprovals(affiliateId: string): Promise<{ success: boolean; added: number; matched: number }> {
    return fetchAdmin('/login-approvals/seed-active', {
      method: 'POST',
      body: JSON.stringify({ affiliateId }),
    });
  },

  async revokeLoginApproval(affiliateId: string, userId: string): Promise<{ success: boolean }> {
    return fetchAdmin(
      `/login-approvals/${encodeURIComponent(affiliateId)}/${encodeURIComponent(userId)}`,
      { method: 'DELETE' }
    );
  },

  async updateLoginApprovalPolicy(enabled: boolean): Promise<LoginApprovalPolicy> {
    return fetchAdmin<LoginApprovalPolicy>('/login-approval-policy', {
      method: 'PUT',
      body: JSON.stringify({ enabled }),
    });
  },

  // ─── 기기 신뢰 — '기기 승인' 탭 (super_admin, server/routes/admin.js) ───
  async getDevices(params: { status?: DeviceListStatus; kind?: 'desktop' | 'web' | ''; q?: string } = {}): Promise<DeviceListResponse> {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.kind) qs.set('kind', params.kind);
    if (params.q) qs.set('q', params.q);
    return fetchAdmin<DeviceListResponse>(`/devices?${qs.toString()}`);
  },

  async getDevicePolicy(): Promise<{ policy: DeviceTrustPolicy }> {
    return fetchAdmin<{ policy: DeviceTrustPolicy }>('/device-policy');
  },

  async updateDevicePolicy(patch: Partial<DeviceTrustPolicy>): Promise<{ policy: DeviceTrustPolicy; purgedSubscriptions: number }> {
    return fetchAdmin('/device-policy', { method: 'PUT', body: JSON.stringify(patch) });
  },

  async getDeviceReadiness(): Promise<DeviceReadiness> {
    return fetchAdmin<DeviceReadiness>('/device-trust/readiness');
  },

  /** 직원 본인 확인이 안 된 요청은 code(직원에게 받은 6자리)가 필요하다. 상한이면 replaceDeviceId */
  async approveDevice(id: number, body: { code?: string; replaceDeviceId?: number } = {}): Promise<{ success: boolean }> {
    return fetchAdmin(`/devices/${id}/approve`, { method: 'POST', body: JSON.stringify(body) });
  },

  async rejectDevice(id: number, reason: string): Promise<{ success: boolean }> {
    return fetchAdmin(`/devices/${id}/reject`, { method: 'POST', body: JSON.stringify({ reason }) });
  },

  async revokeDevice(id: number, reason: string): Promise<{ success: boolean }> {
    return fetchAdmin(`/devices/${id}/revoke`, { method: 'POST', body: JSON.stringify({ reason }) });
  },

  /** 거절·해제·만료 기록 삭제 — 같은 기기로 다시 요청할 수 있게 된다 */
  async deleteDeviceRecord(id: number): Promise<{ success: boolean }> {
    return fetchAdmin(`/devices/${id}`, { method: 'DELETE' });
  },

  // Remote Support (원격지원 — super_admin 전용)
  async getRemoteSupport(): Promise<RemoteSupportAdminState> {
    return fetchAdmin<RemoteSupportAdminState>('/remote-support');
  },

  async grantHelpdeskOperators(input: {
    affiliateId: string;
    users: { userId: string; name?: string; depart?: string }[];
  }): Promise<{ success: boolean; added: number; requested: number }> {
    return fetchAdmin('/remote-support/operators', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  async revokeHelpdeskOperator(affiliateId: string, userId: string): Promise<{ success: boolean }> {
    return fetchAdmin(
      `/remote-support/operators/${encodeURIComponent(affiliateId)}/${encodeURIComponent(userId)}`,
      { method: 'DELETE' }
    );
  },

  // Calendar Editors (회사 일정 담당자 — super_admin 전용)
  async getCalendarEditors(): Promise<CalendarEditorsAdminState> {
    return fetchAdmin<CalendarEditorsAdminState>('/calendar-editors');
  },

  async grantCalendarEditors(input: {
    affiliateId: string;
    users: { userId: string; name?: string; depart?: string }[];
  }): Promise<{ success: boolean; added: number; requested: number }> {
    return fetchAdmin('/calendar-editors', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  async revokeCalendarEditor(affiliateId: string, userId: string): Promise<{ success: boolean }> {
    return fetchAdmin(
      `/calendar-editors/${encodeURIComponent(affiliateId)}/${encodeURIComponent(userId)}`,
      { method: 'DELETE' }
    );
  },

  /** 생략한 스위치는 서버가 그대로 둔다 */
  async updateRemoteSupportPolicy(input: {
    enabled?: boolean;
    controlEnabled?: boolean;
    fileTransferEnabled?: boolean;
  }): Promise<RemoteSupportPolicy> {
    return fetchAdmin<RemoteSupportPolicy>('/remote-support-policy', {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  },

  async getRemoteSupportSessions(filters: { days?: number; operatorId?: string; q?: string } = {}): Promise<RemoteSupportSessionList> {
    const params = new URLSearchParams();
    if (filters.days) params.set('days', String(filters.days));
    if (filters.operatorId) params.set('operatorId', filters.operatorId);
    if (filters.q) params.set('q', filters.q);
    const query = params.toString();
    return fetchAdmin<RemoteSupportSessionList>(`/remote-support/sessions${query ? `?${query}` : ''}`);
  },
};

/** 푸시 발송 카운터 (GET /api/admin/metrics의 push 키). */
export async function getPushMetrics(): Promise<PushMetrics> {
  const res = await fetchAdmin<{ push?: PushMetrics }>('/metrics');
  return res.push || { since: '', channels: {}, recentFailures: [] };
}
